import { useState, useMemo } from "react";
import { Slider } from "@/components/ui/slider";
import { formatEuro } from "@/lib/api";

function calcMonthly(principal, annualRate, months) {
  if (principal <= 0 || months <= 0) return 0;
  const r = annualRate / 100 / 12;
  if (r === 0) return principal / months;
  return (principal * r) / (1 - Math.pow(1 + r, -months));
}

export default function LoanCalculator({ initialPrice = 15000 }) {
  const TOIMITUSKULUT = 399;
  const TILINHOITOMAKSU = 15;
  const [price, setPrice] = useState(initialPrice);
  const [down, setDown] = useState(Math.round(initialPrice * 0.2));
  const rate = 5.9;
  const [months, setMonths] = useState(48);

  const financed = Math.max(price - down, 0) + TOIMITUSKULUT;
  const baseMonthly = useMemo(() => calcMonthly(financed, rate, months), [financed, rate, months]);
  const monthly = baseMonthly + TILINHOITOMAKSU;
  const interest = Math.max(baseMonthly * months - financed, 0);
  const total = down + monthly * months;

  return (
    <div
      className="backdrop-blur-xl bg-white/5 border border-white/10 p-8 md:p-10"
      data-testid="loan-calculator"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="space-y-8">
          <Row label="Auton hinta" value={formatEuro(price)}>
            <Slider value={[price]} min={500} max={80000} step={100}
              onValueChange={([v]) => { setPrice(v); if (down > v) setDown(v); }}
              data-testid="slider-price" />
          </Row>
          <Row label="Käsiraha" value={formatEuro(down)}>
            <Slider value={[down]} min={0} max={price} step={100}
              onValueChange={([v]) => setDown(v)} data-testid="slider-down" />
          </Row>
          <Row label="Korko (vuosi)" value={`${rate.toFixed(1)} %`}>
            <div className="h-2 w-full bg-[#ff5a00]/20 relative" data-testid="rate-locked">
              <div className="absolute inset-y-0 left-0 bg-[#ff5a00]" style={{ width: `${(rate / 20) * 100}%` }} />
            </div>
            <p className="font-mono text-[11px] text-white/40 mt-2">Kiinteä korko 5,9 %</p>
          </Row>
          <Row label="Laina-aika" value={`${months} kk`}>
            <Slider value={[months]} min={12} max={96} step={6}
              onValueChange={([v]) => setMonths(v)} data-testid="slider-months" />
          </Row>
        </div>
        <div className="flex flex-col justify-between border-l border-white/10 lg:pl-10">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-white/50">Arvioitu kuukausierä</p>
            <p className="font-display text-6xl md:text-7xl text-[#ff5a00] tracking-tight mt-2" data-testid="monthly-payment">
              {formatEuro(Math.round(monthly))}
            </p>
            <p className="font-mono text-xs text-white/40 mt-1">/ kk</p>
          </div>
          <div className="mt-8 space-y-3 font-mono text-sm">
            <Line label="Rahoitettava (sis. toimituskulut)" value={formatEuro(Math.round(financed))} />
            <Line label="Toimituskulut" value={formatEuro(TOIMITUSKULUT)} />
            <Line label="Tilinhoitomaksu" value={`${formatEuro(TILINHOITOMAKSU)} / kk`} />
            <Line label="Korkokulut n." value={formatEuro(Math.round(interest))} />
            <Line label="Kokonaissumma n." value={formatEuro(Math.round(total))} />
          </div>
          <p className="mt-6 text-[11px] text-white/30 leading-relaxed">
            Laskelma on suuntaa antava. Toimituskulut 399 € ja tilinhoitomaksu 15 €/kk sisältyvät
            laskelmaan. Lopullinen rahoitus ja sen hinnat voivat poiketa rahoitusyhtiön tarjouksesta.
            Rahoituskumppanimme: Santander, LähiTapiola, Bigbank ja Svea Autorahoitus.
          </p>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value, children }) => (
  <div>
    <div className="flex justify-between items-baseline mb-3">
      <span className="font-mono text-xs uppercase tracking-widest text-white/60">{label}</span>
      <span className="font-mono text-white">{value}</span>
    </div>
    {children}
  </div>
);

const Line = ({ label, value }) => (
  <div className="flex justify-between border-b border-white/10 pb-2">
    <span className="text-white/50">{label}</span>
    <span className="text-white">{value}</span>
  </div>
);
